"use client";

import { useTranslations } from "next-intl";
import { Badge } from "@/components/ui/design-system/badge";
import {
  Modal,
  ModalContent,
  ModalDescription,
  ModalHeader,
  ModalTitle,
} from "@/components/ui/design-system/modal";
import type { RouterOutput } from "@/lib/trpc-client";
import { PortalMarkdown } from "./PortalMarkdown";

type PortalHome = RouterOutput["creatorPortal"]["getPortalHome"];

export type PortalDocId = "compliance" | "brief";

/**
 * The reader for a program's brief and compliance rules.
 *
 * Compliance is the program's ACTIVE rules only. The client's COMPLIANCE
 * knowledge doc is internal agent grounding and never reaches a creator.
 *
 * It used to own its own trigger cards — a bordered pane with two tiles in it,
 * above the fold on Home. Home now lists the same two documents as rows beside
 * the rest of what you do before filming, so this is the reader alone and the
 * caller says which document is open.
 */
export function DocsModal({
  docs,
  complianceRules,
  openDoc,
  onOpenChange,
}: {
  docs: PortalHome["docs"];
  complianceRules: PortalHome["complianceRules"];
  openDoc: PortalDocId | null;
  onOpenChange: (open: boolean) => void;
}) {
  const t = useTranslations("home");
  const hasRules = complianceRules.length > 0;

  return (
    <Modal open={openDoc !== null} onOpenChange={onOpenChange}>
      {/* Wears `ds-next` itself: Radix portals the content to the body,
          outside the route's `ds-next` div, and without the class the reader
          renders in Framework's palette. Same reason as SettingsDialog. */}
      <ModalContent size="lg" className="ds-next">
        <ModalHeader>
          <ModalTitle>
            {openDoc === "compliance"
              ? t("docs.compliance.modalTitle")
              : t("docs.brief.modalTitle")}
          </ModalTitle>
          <ModalDescription>
            {openDoc === "compliance"
              ? t("docs.compliance.modalDescription")
              : t("docs.brief.modalDescription")}
          </ModalDescription>
        </ModalHeader>
        <div className="max-h-[60vh] overflow-y-auto pr-nf-ds-2">
          {openDoc === "compliance" ? (
            <div className="flex flex-col gap-nf-ds-4">
              {hasRules ? (
                <div className="flex flex-col gap-nf-ds-2">
                  <span className="font-nf-ds-mono text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground leading-none">
                    {t("docs.compliance.enforcedRules")}
                  </span>
                  <ul className="divide-y divide-border border-y border-border">
                    {complianceRules.map((rule) => (
                      <li
                        key={rule.id}
                        className="flex items-start gap-nf-ds-3 py-nf-ds-3"
                      >
                        <Badge
                          variant={
                            rule.ruleType === "PROHIBITED" ? "err" : "ok"
                          }
                        >
                          {rule.ruleType === "PROHIBITED"
                            ? t("docs.rule.prohibited")
                            : rule.ruleType === "REQUIRED"
                              ? t("docs.rule.required")
                              : t("docs.rule.conditional")}
                        </Badge>
                        <span className="font-nf-ds-sans text-[13px] leading-[1.5]">
                          {rule.ruleText}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : docs.brief ? (
            <PortalMarkdown>{docs.brief}</PortalMarkdown>
          ) : null}
        </div>
      </ModalContent>
    </Modal>
  );
}
